import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListSubheader,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  ENTERPRISE_ADDINS_ACCESS,
  ENTERPRISE_ADDINS_URL,
  MASTER_DATA,
  VENDOR_DROPDOWN,
} from "../../constants/api-constants";
import ApiServices from "../../services/ApiServices";
import "../AddInsServices/AddInsList.css";
import DataTable from "../Tables/DataTable/DataTable";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CheckboxList from "../../common/CheckboxList/CheckboxList";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ClearSelectionData } from "../../utilities/SelectionFilter";
import TableColumnModal from "../../common/TableColumnModal/TableColumnModal";
import { listofAddInsEnterpriseDataColumns } from "../../common/TableColumnModal/DefaultDataColumns";
import { useLoading } from "../../hooks/useLoader";
import { toast } from "react-toastify";
import { useHistory, useParams } from "react-router-dom";
import AddInsSubscriptionAddToCart from "./Common/AddInsSubscriptionAddToCart";
import { SUBSCRIPTION_STATUS, SUBSCRIPTION_STATUS_OPTIONS } from "./constants";
import {
  isEnterpriseUserRole,
  isSuperAdminOrAdminRole,
} from "../../utilities/UserRoleUtils";
import Confirmation from "../../common/Confirmation/Confirmation";
import getConfirmAccessTitleText from "./Common/getConfirmAccessTitleText";
import AccessGrantWithDate from "./Common/AccessGrantWithDate";

export { SUBSCRIPTION_STATUS, SUBSCRIPTION_STATUS_OPTIONS };

/*istanbul ignore next */
const AddInsServicesTab = ({ permissions }) => {
  const { id } = useParams();
  const history = useHistory();
  const { showLoader } = useLoading();
  const canShowCart = isEnterpriseUserRole();
  const canViewAccessColumn = isSuperAdminOrAdminRole();

  const [selectedColumns, setSelectedColumns] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [tableColumnModalVisibility, setTableColumnModalVisibility] =
    useState(false);
  const [addInsTableData, setAddInsTableData] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [selectedAddInTypes, setSelectedAddInTypes] = useState([]);
  const [selectedCostTypes, setSelectedCostTypes] = useState([]);
  const [selectedSubscriptionStatus, setSelectedSubscriptionStatus] = useState(
    [],
  );
  const [cartCount, setCartCount] = useState(0);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [selectedAddIn, setSelectedAddIn] = useState(null);
  const [cartAddIns, setCartAddIns] = useState([]);
  const [pendingAccessToggle, setPendingAccessToggle] = useState(null);
  const [showGrantAccessPopup, setShowGrantAccessPopup] = useState(false);
  const [showRevokeAccessConfirmation, setShowRevokeAccessConfirmation] =
    useState(false);

  // Dynamic filter options
  const [vendorOptions, setVendorOptions] = useState([]);
  const [addInTypeOptions, setAddInTypeOptions] = useState([]);
  const [costTypeOptions, setCostTypeOptions] = useState([]);

  // Loading states for coordinated loading
  const [isFilterOptionsLoading, setIsFilterOptionsLoading] = useState(true);
  const [isTableDataLoading, setIsTableDataLoading] = useState(true);
  // Load filter options from API
  const loadFilterOptions = async () => {
    setIsFilterOptionsLoading(true);
    try {
      const [vendorResponse, masterDataResponse] = await Promise.all([
        ApiServices.httpGet(`${VENDOR_DROPDOWN}?enterpriseGroupId=${id || 0}`),
        ApiServices.httpGet(`${MASTER_DATA}?include=enumMasterData`),
      ]);

      if (vendorResponse?.responseStatus?.isSuccess) {
        let vendors = [];
        if (Array.isArray(vendorResponse?.result)) {
          vendors = vendorResponse.result;
        } else if (Array.isArray(vendorResponse?.result?.records)) {
          vendors = vendorResponse.result.records;
        }
        setVendorOptions(
          vendors
            .map((vendor) => ({
              value: vendor?.vendorId ?? vendor?.id ?? vendor?.value,
              label: vendor?.name ?? vendor?.label ?? vendor?.displayName,
            }))
            .filter((vendor) => vendor.value !== undefined && vendor.label),
        );
      }

      const enumMasterData = masterDataResponse?.result?.enumMasterData || {};
      const addInTypes = enumMasterData.addInType || [];
      const options = addInTypes.map((item) => ({
        label: item.value,
        value: Number(item.key),
      }));
      setAddInTypeOptions(options);

      const addInServiceCost = enumMasterData.addInServiceCost || [];
      const costOptions = addInServiceCost.map((item) => ({
        label: item.value,
        value: Number(item.key),
      }));
      setCostTypeOptions(costOptions);
    } catch (error) {
      console.error("Failed to load filter options", error);
    } finally {
      setIsFilterOptionsLoading(false);
    }
  };

  useEffect(() => {
    loadFilterOptions();
  }, []);

  // Coordinate loader: show until all APIs complete
  useEffect(() => {
    const isLoading = isFilterOptionsLoading || isTableDataLoading;
    console.log("Loader state updated:", isLoading);
    showLoader(isTableDataLoading);
  }, [isFilterOptionsLoading, isTableDataLoading, showLoader]);

  const [query, setQuery] = useState({
    searchText: "",
    SortType: "desc",
    IgnorePaging: false,
    SortColumn: "name",
    PageNumber: 1,
    PageSize: 10,
    Filter: {},
  });

  // Transform data with labels for display
  const transformAddInsData = (addIns) => {
    return addIns.map((addIn) => {
      const isSubscribed =
        Number(addIn.subscriptionStatus) === SUBSCRIPTION_STATUS.SUBSCRIBED ||
        String(addIn.subscriptionStatusText || "").toLowerCase() ===
          "subscribed";

      return {
        ...addIn,
        addInTypeLabel: addIn.addInTypeText,
        costTypeLabel: addIn.costTypeText,
        hasAdminAccess: isSubscribed ? true : Boolean(addIn.hasAdminAccess),
        status: addIn.subscriptionStatusText,
        isAddedInCart: addIn.isAddedInCart || false,
      };
    });
  };

  useEffect(() => {
    loadAddInsData();
  }, [query]);

  useEffect(() => {
    setSelectionColumns();
  }, [query]);

  const open = Boolean(anchorEl);

  const onFilterVendorChangeHandler = (values) => {
    setSelectedVendors(values);
  };

  const onFilterAddInTypeChangeHandler = (values) => {
    setSelectedAddInTypes(values);
  };

  const onFilterCostTypeChangeHandler = (values) => {
    setSelectedCostTypes(values);
  };

  const onFilterSubscriptionStatusChangeHandler = (values) => {
    setSelectedSubscriptionStatus(values);
  };

  const applyFilterHandler = (Filter) => {
    ClearSelectionData();
    setQuery((prevState) => {
      return {
        ...prevState,
        PageNumber: 1,
        Filter: {
          vendorIds: selectedVendors,
          addInType: selectedAddInTypes,
          costType: selectedCostTypes,
          subscriptionStatus: selectedSubscriptionStatus,
        },
      };
    });
  };

  const clearFilterHandler = () => {
    setSelectedVendors([]);
    setSelectedAddInTypes([]);
    setSelectedCostTypes([]);
    setSelectedSubscriptionStatus([]);
    // Reset query filter and reload list
    setQuery((prevState) => ({
      ...prevState,
      PageNumber: 1,
      Filter: {
        vendorIds: [],
        addInType: [],
        costType: [],
        subscriptionStatus: [],
      },
    }));
    // Optionally call loadAddInsData directly if needed
    // loadAddInsData();
  };

  const onSearchHandler = (event) => {
    ClearSelectionData();
    setTimeout(() => {
      setQuery((prevState) => {
        return {
          ...prevState,
          PageNumber: 1,
          searchText: event.target.value,
        };
      });
    }, 1000);
  };

  const sortHandler = (sortOrder, sortBy) => {
    setQuery((prevState) => {
      if (prevState.SortColumn === sortBy) {
        return {
          ...prevState,
          SortType: sortOrder === "asc" ? "desc" : "asc",
        };
      } else {
        return {
          ...prevState,
          SortColumn: sortBy,
          SortType: "asc",
        };
      }
    });
  };

  const handleChangePageNumber = (event, newPage) => {
    setQuery((prevState) => {
      return { ...prevState, PageNumber: newPage };
    });
  };

  /*istanbul ignore next */
  const pageSizeChangeHandler = (PageSize) => {
    setQuery((prevState) => {
      return { ...prevState, PageSize: PageSize, PageNumber: 1 };
    });
  };

  const onView = (addIn) => {
    // Open the Add-in details popup. The popup itself decides whether the
    // primary CTA is "Add to Cart" (unpurchased) or hides it (purchased -> view mode).
    setSelectedAddIn(addIn);
    setCartModalOpen(true);
  };

  const onCancelTableColumnSelection = () => {
    setSelectionColumns();
    setTableColumnModalVisibility(false);
  };

  const onSaveTableColumnSelection = (componentName, columns) => {
    let oldData = localStorage.getItem("selectedColumns")
      ? JSON.parse(localStorage.getItem("selectedColumns"))
      : null;
    if (oldData) {
      let data = { ...oldData, listOfAddInsEnterpriseDataColumns: columns };
      localStorage.setItem("selectedColumns", JSON.stringify(data));
    }
    setSelectionColumns();
    setTableColumnModalVisibility(false);
  };

  const setSelectionColumns = () => {
    const baseColumns = canViewAccessColumn
      ? listofAddInsEnterpriseDataColumns
      : listofAddInsEnterpriseDataColumns.filter(
          (column) => column.fieldName !== "hasAdminAccess",
        );

    let selectedCols = localStorage.getItem("selectedColumns")
      ? JSON.parse(localStorage.getItem("selectedColumns"))
      : null;
    let adminCols = selectedCols?.listOfAddInsEnterpriseDataColumns
      ? selectedCols?.listOfAddInsEnterpriseDataColumns
      : null;
    if (adminCols) {
      const selectedByLabel = new Map(
        adminCols.map((column) => [column.label, column]),
      );
      const normalizedColumns = baseColumns.map((column) => {
        const storedColumn = selectedByLabel.get(column.label);
        return storedColumn
          ? { ...column, isSelected: storedColumn.isSelected }
          : { ...column };
      });
      setSelectedColumns(normalizedColumns);
    } else {
      setSelectedColumns(
        baseColumns.map((column) => ({
          ...column,
        })),
      );
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getSortColumn = (sortColumn) => {
    switch (sortColumn) {
      case "costTypeText":
        return "costType";
      case "status":
        return "SubscriptionStatus";
      default:
        return sortColumn || "";
    }
  };

  // API integration for Add-Ins list
  const loadAddInsData = async () => {
    setIsTableDataLoading(true);
    try {
      // Get enterpriseId from props or context (update as needed)
      const enterpriseId = id || 0;
      const payload = {
        isShowChildUserData: true,
        searchText: query.searchText || "",
        selectedIds: [],
        pageSize: query?.PageSize || 10,
        pageNumber: query?.PageNumber || 1,
        ignorePaging: query?.IgnorePaging || false,
        sortColumn: getSortColumn(query?.SortColumn),
        sortType: query?.SortType || "",
        filter: {
          enterpriseGroupId: enterpriseId,
          vendorIds: selectedVendors,
          addInTypes: selectedAddInTypes,
          costTypes: selectedCostTypes,
          statuses: selectedSubscriptionStatus,
        },
        isSearchInAllQuery: true,
      };

      const url = ENTERPRISE_ADDINS_URL(enterpriseId);
      const data = await ApiServices.httpPost(url, payload);

      if (!data.responseStatus?.isSuccess) {
        throw new Error(data.responseStatus?.error || "API returned error");
      }

      const records = data.result?.records || [];
      setAddInsTableData(transformAddInsData(records));
      setTotalPages(data.result?.totalPages || 0);
      setPageSize(data.result?.pageSize || 0);
      setCartCount(data.result?.totalCartItems || 0); // Reset cart count on new data load
    } catch (e) {
      setAddInsTableData([]);
      setTotalPages(0);
      setPageSize(0);
      toast.error(e.message || "Failed to load Add-ins data");
      console.error(e);
    } finally {
      setIsTableDataLoading(false);
      showLoader(false);
    }
  };

  const updateAccess = async ({
    addInId,
    hasAccess,
    effectiveFromDate,
    effectiveToDate,
    reason,
  }) => {
    const enterpriseGroupId = Number(id) || 0;

    if (!enterpriseGroupId || !Number(addInId)) {
      toast.error("Unable to update access. Missing enterprise or add-in ID.");
      return false;
    }

    try {
      showLoader(true);
      const normalizedHasAccess = Boolean(hasAccess);
      const payload = {
        hasAccess: normalizedHasAccess,
      };

      if (normalizedHasAccess) {
        if (effectiveFromDate) {
          payload.effectiveFromDate = effectiveFromDate;
        }
        if (effectiveToDate) {
          payload.effectiveToDate = effectiveToDate;
        }
      } else if (reason) {
        payload.reason = reason;
      }

      const response = await ApiServices.httpPut(
        ENTERPRISE_ADDINS_ACCESS(enterpriseGroupId, Number(addInId)),
        payload,
      );

      if (response?.responseStatus?.isSuccess) {
        toast.success(
          response?.responseStatus?.description ||
            "Access updated successfully.",
        );
        return true;
      } else {
        toast.error(
          response?.responseStatus?.error || "Failed to update access.",
        );
        return false;
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update access.");
      return false;
    } finally {
      showLoader(false);
      loadAddInsData();
    }
  };

  const handleAccessToggle = (_fieldName, _userId, _status, event, row) => {
    if (!canViewAccessColumn) {
      toast.error("Only Super Admin can change add-in access.");
      return;
    }

    const currentValue = Boolean(row?.hasAdminAccess);
    const nextHasAccess =
      typeof event?.target?.checked === "boolean"
        ? event.target.checked
        : !currentValue;

    const pendingToggle = {
      addInId: row?.addInId,
      hasAccess: nextHasAccess,
    };

    setPendingAccessToggle(pendingToggle);
    if (nextHasAccess) {
      setShowGrantAccessPopup(true);
      return;
    }
    setShowRevokeAccessConfirmation(true);
  };

  const resetAccessToggleState = () => {
    setPendingAccessToggle(null);
    setShowGrantAccessPopup(false);
    setShowRevokeAccessConfirmation(false);
  };

  const handleConfirmRevokeAccessToggle = async (_event, reason) => {
    if (!pendingAccessToggle) return;
    const payload = {
      ...pendingAccessToggle,
      reason: reason || null,
    };
    resetAccessToggleState();
    await updateAccess(payload);
  };

  const handleConfirmGrantAccessToggle = async ({
    effectiveFromDate,
    effectiveToDate,
  }) => {
    if (!pendingAccessToggle) return;
    const payload = {
      ...pendingAccessToggle,
      effectiveFromDate,
      effectiveToDate,
      reason: null,
    };
    resetAccessToggleState();
    await updateAccess(payload);
  };

  const handleCancelAccessToggle = () => {
    resetAccessToggleState();
  };

  const onActionHandler = (actionName, addIn) => {
    if (actionName === "AddToCart") {
      if (addIn.subscriptionStatus === 1) {
        toast.warning("This Add-In is already subscribed");
      } else if (addIn.subscriptionStatus === 2) {
        toast.info(
          "This subscription has expired. Renew to continue using it.",
        );
      } else {
        // Add to cart logic
        toast.success(`${addIn.name} added to cart`);
        console.log("Add to cart:", addIn);
      }
    }
  };

  const handleCartClick = () => {
    history.push("/cart/checkout");
  };

  const handleAddToCart = (cartData) => {
    // Check if already in cart
    const isAlreadyInCart = cartAddIns.some(
      (item) => item.addInId === cartData.addInId,
    );

    if (isAlreadyInCart && cartData.cartItemId) {
      // Update existing cart item — keep cartCount unchanged
      setCartAddIns((prevItems) =>
        prevItems.map((item) =>
          item.id === cartData.cartItemId
            ? { ...item, addInSubscriptionId: cartData.addInSubscriptionId }
            : item,
        ),
      );
      toast.success("Cart item updated successfully");
    } else {
      // Add new item to cart — increment cartCount
      setCartAddIns((prevItems) => [
        ...prevItems,
        { id: Date.now(), ...cartData },
      ]);
      setCartCount((prevCount) => prevCount + 1);
      toast.success(`${selectedAddIn?.name} added to cart`);
    }

    // Mark the matching row as added so the list cart icon flips to the
    // "in cart" variant (red AddShoppingCart) like the Purchase screen.
    setAddInsTableData((prevRows) =>
      prevRows.map((row) =>
        row.addInId === cartData.addInId
          ? { ...row, isAddedInCart: true }
          : row,
      ),
    );

    // Keep the Add-in details popup open so the user can continue browsing
    // or navigate to the cart explicitly.
    console.log("Cart Data:", cartData);
  };

  const handleRemoveFromCart = () => {
    setCartAddIns((prevItems) =>
      prevItems.filter((item) => item.addInId !== selectedAddIn?.addInId),
    );
    setCartCount((prevCount) => Math.max(0, prevCount - 1));
    setAddInsTableData((prevRows) =>
      prevRows.map((row) =>
        row.addInId === selectedAddIn?.addInId
          ? { ...row, isAddedInCart: false }
          : row,
      ),
    );
    console.log("Removed from cart:", selectedAddIn?.addInId);
  };

  const handleGoToCart = () => {
    setCartModalOpen(false);
    history.push("/cart/checkout");
  };

  const handleCloseCartModal = () => {
    setCartModalOpen(false);
    setSelectedAddIn(null);
  };

  return (
    <>
      <Confirmation
        open={showRevokeAccessConfirmation}
        handleClose={handleCancelAccessToggle}
        title={getConfirmAccessTitleText(true)}
        showReason
        onCancel={handleCancelAccessToggle}
        onConfirm={handleConfirmRevokeAccessToggle}
        cancelButtonLabel="No"
        confirmButtonLabel="Yes"
      />
      <AccessGrantWithDate
        open={showGrantAccessPopup}
        handleClose={handleCancelAccessToggle}
        title={getConfirmAccessTitleText(false)}
        onCancel={handleCancelAccessToggle}
        onConfirm={handleConfirmGrantAccessToggle}
        cancelButtonLabel="No"
        confirmButtonLabel="Yes"
      />
      <Grid
        spacing={2}
        container
        className="reports-container eg-addInsTab-container"
      >
        <Grid container item xs={12}>
          <Grid item xs={10}>
            <Box pt={2} pr={4} pl={4} pb={0}>
              <h5>Add-Ins / Services</h5>
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={12} justifyContent={"flex-end"} display={"flex"} mr={5}>
          <div className="add-welcome-widgets">
            <div>
              <Popover
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                id={"filter-popover"}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
              >
                <div className="expanded-filter">
                  <div className="filter-options">
                    <List
                      sx={{
                        width: "100%",
                        maxWidth: 360,
                        bgcolor: "background.paper",
                      }}
                      component="nav"
                      aria-labelledby="nested-list-subheader"
                      subheader={
                        <ListSubheader
                          component="div"
                          id="nested-list-subheader"
                        >
                          Filters will be applied to your grid
                        </ListSubheader>
                      }
                    >
                      <Accordion>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel-vendor-content"
                          id="panel-vendor-header"
                        >
                          <Typography sx={{ width: "33%", flexShrink: 0 }}>
                            Vendor
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <CheckboxList
                            id="vendor"
                            options={vendorOptions}
                            onChange={onFilterVendorChangeHandler}
                            selectedValues={selectedVendors}
                          />
                        </AccordionDetails>
                      </Accordion>

                      <Accordion>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel-type-content"
                          id="panel-type-header"
                        >
                          <Typography sx={{ width: "33%", flexShrink: 0 }}>
                            Add-in Type
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <CheckboxList
                            id="add-in-type"
                            options={addInTypeOptions}
                            onChange={onFilterAddInTypeChangeHandler}
                            selectedValues={selectedAddInTypes}
                          />
                        </AccordionDetails>
                      </Accordion>

                      <Accordion>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel-cost-content"
                          id="panel-cost-header"
                        >
                          <Typography sx={{ width: "33%", flexShrink: 0 }}>
                            Cost Type
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <CheckboxList
                            id="cost-type"
                            options={costTypeOptions}
                            onChange={onFilterCostTypeChangeHandler}
                            selectedValues={selectedCostTypes}
                          />
                        </AccordionDetails>
                      </Accordion>

                      <Accordion>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel-subscription-content"
                          id="panel-subscription-header"
                        >
                          <Typography sx={{ width: "33%", flexShrink: 0 }}>
                            Status
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <CheckboxList
                            id="subscription-status"
                            options={SUBSCRIPTION_STATUS_OPTIONS}
                            onChange={onFilterSubscriptionStatusChangeHandler}
                            selectedValues={selectedSubscriptionStatus}
                          />
                        </AccordionDetails>
                      </Accordion>
                    </List>
                  </div>
                  <div className="modal-footer">
                    <Button
                      variant="outlined"
                      className="action-button mr-4"
                      onClick={clearFilterHandler}
                    >
                      Clear
                    </Button>
                    <Button
                      variant="contained"
                      className="action-button"
                      onClick={() => {
                        applyFilterHandler();
                        handleClose();
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </Popover>
            </div>

            <Tooltip placement="bottom" title="Filter">
              <IconButton aria-label="filter-icon" onClick={handleClick}>
                <FilterAltOutlinedIcon />
              </IconButton>
            </Tooltip>

            <Tooltip placement="bottom" title="Change View">
              <BorderColorIcon
                data-testid="TableColumnSelectionIcon"
                onClick={() => {
                  setTableColumnModalVisibility(true);
                }}
              />
            </Tooltip>
          </div>
        </Grid>

        <Grid item xs={12}>
          <Box pr={4} pl={4} pb={0}>
            <DataTable
              uniqueKey="addInId"
              query={query}
              columns={selectedColumns}
              onView={(data) => onView(data)}
              adminTable={addInsTableData}
              onSearch={onSearchHandler}
              onSort={sortHandler}
              onPageSizeChange={pageSizeChangeHandler}
              handleChangePage={handleChangePageNumber}
              totalPages={totalPages}
              pageSize={pageSize}
              pageSizeOptions={[10, 25, 50, 100]}
              checkboxesNotRequired={true}
              permissions={permissions}
              actionHandler={onActionHandler}
              showView={true}
              useCartIconForUnpurchased={true}
              showMenu={false}
              showToggle={true}
              showEdit={false}
              onToggle={handleAccessToggle}
              showCart={canShowCart}
              cartCount={cartCount}
              handleCartClick={handleCartClick}
              showSettingAction={true}
              onSettings={(row) => {
                const enterpriseId = Number(id) || 0;
                const addInsId = Number(row?.addInId) || 0;

                if (!enterpriseId || !addInsId) {
                  toast.error(
                    "Unable to open settings. Missing enterprise or add-in ID.",
                  );
                  return;
                }

                history.push({
                  pathname: "/add-ins/service/configuration",
                  search: `?enterpriseId=${enterpriseId}&addInsId=${addInsId}`,
                  state: { enterpriseId, addInsId },
                });
              }}
              disableSettingAction={(row) =>
                row.subscriptionStatus === 0 ||
                row.subscriptionStatusText === "Not Purchased"
              }
            />

            {tableColumnModalVisibility && (
              <TableColumnModal
                isOpen={tableColumnModalVisibility}
                onCancelTableColumnSelection={onCancelTableColumnSelection}
                onSaveTableColumnSelection={onSaveTableColumnSelection}
                selection={selectedColumns}
              />
            )}

            {/* Add-ins Subscription Modal */}
            <AddInsSubscriptionAddToCart
              open={cartModalOpen}
              enterpriseId={id}
              addInsId={selectedAddIn?.addInId}
              alreadyInCart={selectedAddIn?.subscriptionStatus === 1}
              cartItemId={selectedAddIn?.id}
              onClose={handleCloseCartModal}
              onAddToCart={handleAddToCart}
              onRemoveFromCart={handleRemoveFromCart}
              onGoToCart={handleGoToCart}
              onUpdateAccess={updateAccess}
              cartCount={cartCount}
            />
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

AddInsServicesTab.propTypes = {
  permissions: PropTypes.any,
};

export default AddInsServicesTab;
